/**
 * Reports Data Mapper
 *
 * Data transformation between DTOs and domain models
 */

import {
  Marketer,
  MarketerStoresReport,
  MarketerFinancialReport,
  ExportResponse,
  MarketerInfo,
  StoreFinancial,
  ProductSale,
  FinancialSummary,
  TopCustomer
} from '../models/report.model';
import {
  MarketerDTO,
  MarketerStoresReportDTO,
  MarketerFinancialReportDTO,
  ExportResponseDTO,
  MarketerInfoDTO,
  StoreFinancialDTO,
  ProductSaleDTO,
  FinancialSummaryDTO,
  TopCustomerDTO
} from '../models/report.dto';

export const reportMapper = {
  toMarketer (dto: MarketerDTO): Marketer {
    return {
      id: dto.id,
      name: dto.name || 'غير محدد',
      territory: dto.territory || null,
      status: dto.status || 'inactive',
      total_stores: dto.total_stores || 0,
      total_sales: dto.total_sales || 0
    };
  },

  toMarketerInfo (dto: MarketerInfoDTO): MarketerInfo {
    return {
      id: dto.id,
      name: dto.name || 'غير محدد',
      territory: dto.territory || null,
      status: dto.status || 'inactive'
    };
  },

  toStoreFinancial (dto: StoreFinancialDTO): StoreFinancial {
    return {
      store_id: dto.store_id,
      store_name: dto.store_name,
      invoice_value: dto.invoice_value,
      paid_amount: dto.paid_amount,
      remaining_balance: dto.remaining_balance,
      last_payment_date: dto.last_payment_date,
      payment_status: dto.payment_status
    };
  },

  toProductSale (dto: ProductSaleDTO): ProductSale {
    return {
      product_id: dto.product_id,
      product_name: dto.product_name,
      quantity_sold: dto.quantity_sold,
      unit_price: dto.unit_price,
      total_revenue: dto.total_revenue
    };
  },

  toFinancialSummary (dto: FinancialSummaryDTO): FinancialSummary {
    return {
      total_stores: dto.total_stores,
      total_invoices: dto.total_invoices,
      total_paid: dto.total_paid,
      total_outstanding: dto.total_outstanding,
      collection_rate: dto.collection_rate
    };
  },

  toTopCustomer (dto: TopCustomerDTO): TopCustomer {
    return {
      customer_name: dto.customer_name,
      total_amount: dto.total_amount,
      paid_amount: dto.paid_amount
    };
  },

  toMarketerStoresReport (dto: MarketerStoresReportDTO): MarketerStoresReport {
    return {
      marketer_info: reportMapper.toMarketerInfo(dto.marketer_info),
      stores_financial: dto.stores_financial.map(store => reportMapper.toStoreFinancial(store)),
      product_sales: dto.product_sales.map(product => reportMapper.toProductSale(product)),
      summary: reportMapper.toFinancialSummary(dto.summary)
    };
  },

  toMarketerFinancialReport (dto: MarketerFinancialReportDTO): MarketerFinancialReport {
    return {
      marketer_info: reportMapper.toMarketerInfo(dto.marketer_info),
      financial_summary: {
        ...reportMapper.toFinancialSummary(dto.financial_summary),
        total_orders: dto.financial_summary.total_orders,
        average_order_value: dto.financial_summary.average_order_value
      },
      top_customers: dto.top_customers.map(customer => reportMapper.toTopCustomer(customer))
    };
  },

  toExportResponse (dto: ExportResponseDTO): ExportResponse {
    return {
      download_url: dto.download_url,
      format: dto.format,
      generated_at: dto.generated_at
    };
  },

  // Stock report mappers
  toMarketerStockItem (dto: import('../models/report.dto').MarketerStockItemDTO | any): import('../models/report.model').MarketerStockItem {
    return {
      product_id: dto.product_id,
      product_name: dto.product_name,
      product_sku: dto.product_sku ?? dto.product_barcode,
      brand_name: dto.brand_name ?? dto.product_brand,
      quantity_available: dto.quantity_available ?? dto.quantity,
      quantity_reserved: dto.quantity_reserved,
      last_update_at: dto.last_update_at
    };
  },

  toMarketerStockReport (
    dto: import('../models/report.dto').MarketerStockReportDTO | Record<string, unknown>
  ): import('../models/report.model').MarketerStockReport {
    const data = dto as Record<string, any>;
    const products = (data.products as unknown[]) || (data.items as unknown[]) || [];
    const marketerInfo = (data as any).marketer_info
      ? reportMapper.toMarketerInfo((data as any).marketer_info)
      : {id: (data as any).marketer_id || 0, name: '', territory: null, status: 'active' as const};

    return {
      marketer_info: marketerInfo,
      items: (products || []).map((item: unknown) => reportMapper.toMarketerStockItem(item as any)),
      total_items: data.total_items || (products ? products.length : 0),
      last_synced_at: data.last_synced_at
    };
  }
};

